import { View } from "@westpierstudio/luma/mvc";
import { ElasticCache } from "@westpierstudio/luma/utils/elastic-cache";
import { SpineAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { GameServices } from "game/GameServices";
import { Reel } from "./Reel";
import { ReelsLayout, SpinConfig } from "./ReelsModel";
import { ReelStrips } from "servertypes/gameevents/OpenGame";
import { BonusSymbolID, ReelSetSymbols, ReelSymbols, SymbolPos } from "servertypes/SharedTypes";
import { cloneDeep } from "lodash";
import { Signal } from "type-signals";
import { drawRectCentered } from "utils/Misc";
import { Container } from "pixi.js";
import { GameEvent } from "servertypes/ServerEvents";
import { AnticipationView } from "components/AnticipationView";
import { getSoundPlayer } from "sound/SoundLocator";
import { Spine } from "pixi-spine";
import { ReelsBezel, ReelsBezelOverlay } from "./ReelsBezel";
import { CrossbowBonus } from "components/CrossbowBonus";
import { Jackpot } from "components/jackpot/Jackpot";
import { CrossbowMultipliers } from "components/CrossbowMultipliers";
import { WinLines } from "components/winlines/WinLines";
import { ReelsCharacters } from "./ReelsCharacters";

class ReelsSignals {
    reelStop = new Signal<(reelIndex: number) => void>();
    spinComplete = new Signal();
    scatterWinComplete = new Signal();
    slamStop = new Signal();
    reelAnticipateSound = new Signal();
}

type SymbolsSpec = { [symbolName: string]: SpineAssetSpec };

export type SymbolCache = { [key: string]: ElasticCache<Spine> };

export class Reels extends View {

    private symbolCache: SymbolCache;
    private reelContainer!: Container;
    private reels: Reel[] = [];
    private spinConfig!: SpinConfig;
    private layout!: ReelsLayout;
    private anticipationView!: AnticipationView;
    private crossbowMultipliers!: CrossbowMultipliers;
    private reelsBezel!: ReelsBezel;
    private reelsCharacters!: ReelsCharacters;
    private reelsBezelOverlay!: ReelsBezelOverlay;
    private defaultBonusReels!: ReelSetSymbols;

    private _lastGameEvent?: GameEvent;

    crossbowBonus!: CrossbowBonus;
    winlines!: WinLines;
    jackpot!: Jackpot;

    signals = new ReelsSignals();

    constructor(protected override services: GameServices) {
        super(services);

        this.symbolCache = this.setupSymbolCache();

        this.services.layout.onReorient.add(() => this.reorient());
    }

    //#region setup

    setup(layout: ReelsLayout) {

        const { factory, layout: gameLayout } = this.services;

        factory.applySpec(this, { scale: layout.scale });
        factory.applySpec(this, layout.position[gameLayout.orientation]);

        this.setupCharacters(layout);
        this.setupBezel(layout);
        this.setupReels(layout);
        this.setupMask(layout);
        this.setupBezelOverlay(layout);
        this.setupJackpot(layout);
        this.setupCrossbow();
        this.setupWinlines();
        this.setupAnticipation();
        this.setupMultipliers();
    }

    private setupBezelOverlay(layout: ReelsLayout) {
        this.addChild(this.reelsBezelOverlay = new ReelsBezelOverlay(this.services, layout));
    }

    private setupBezel(layout: ReelsLayout) {
        this.addChild(this.reelsBezel = new ReelsBezel(this.services, layout));
    }

    private setupCharacters(layout: ReelsLayout) {
        this.addChild(this.reelsCharacters = new ReelsCharacters(this.services, layout));
    }

    private setupReels(layout: ReelsLayout) {
        this.addChild(this.reelContainer = new Container());
        this.reelContainer.name = `Reel Container`;

        this.layout = layout;
        const { positions, reelCount, symbolSize } = layout;

        let reel;

        for (let i = 0; i < reelCount; i++) {

            reel = new Reel(this.services, i, positions[i], symbolSize, this.symbolCache);

            reel.signals.reelStop.add(this.reelStopped, this);
            reel.signals.scatterWinComplete.add((_reelIndex: number) => {
                this.signals.scatterWinComplete.dispatch();
            });

            this.reelContainer.addChild(reel);
            this.reels.push(reel);
        }
    }

    private setupJackpot(reelsLayout: ReelsLayout) {
        this.addChild(this.jackpot = new Jackpot(this.services, reelsLayout));
    }

    private setupCrossbow() {
        this.addChild(this.crossbowBonus = new CrossbowBonus(this.services));
    }

    private setupWinlines() {
        this.addChild(this.winlines = new WinLines(this.services));
    }

    private setupAnticipation() {
        this.addChild(this.anticipationView = new AnticipationView(this.services));
        this.reels.forEach(reel => {
            reel.signals.reelAnticipateStart.add((pos) => this.anticipationView.show(pos));
        });
        this.signals.spinComplete.add(() => this.anticipationView.hide());
        this.signals.slamStop.add(() => this.anticipationView.hide());
    }

    private setupMultipliers() {
        this.addChild(this.crossbowMultipliers = new CrossbowMultipliers(this.services));
        this.crossbowBonus.setupMultipliers(this.crossbowMultipliers);
    }

    //#endregion

    setCurrentReelSymbols(reels: ReelSetSymbols): void {

        for (const [reelIndex, value] of reels.entries()) {
            this.reels[reelIndex].setSymbols(value);
        }
    }

    setReelStrips(reelStrips: ReelStrips): void {

        for (const [reelIndex, value] of reelStrips.entries()) {
            this.reels[reelIndex].setReelStrip(value);
        }
    }

    setDefaultBonusReels(defaultBonusReels: ReelSetSymbols) {
        this.defaultBonusReels = defaultBonusReels;
    }

    setCurrentReelSymbolsToBonus() {
        this.setCurrentReelSymbols(this.defaultBonusReels);
    }

    bonusHitReels(spinResult: ReelSetSymbols, eventType: GameEvent): number[] {
        const bonusNeeded = 3;
        const bonusReels: number[] = [];
        let bonusCount = eventType === GameEvent.INITIAL_SPIN ? 0 : 1;//Initial spin plays bonusHit1-5, freespin plays 2-6
        for (const reelResult of spinResult) {
            bonusReels.push(reelResult.includes(BonusSymbolID.B1) ? ++bonusCount : 0);
        }
        //does not play bonus hit if no chance to get bonus
        if (bonusReels[4] < bonusNeeded) bonusReels[4] = 0;
        if (bonusReels[3] < bonusNeeded - 1) bonusReels[3] = 0;
        return bonusReels;
    }

    setSpinConfig(spinConfig: SpinConfig, spinResult: ReelSetSymbols, eventType: GameEvent): void {
        const bonusHitReels = this.bonusHitReels(spinResult, eventType);
        const anticipate = this.getAnticipationReels(spinResult, eventType);
        const anticipationTime = spinConfig.anticipationTime;
        let accumulatedAnticipation = 0;
        for (const [reelIndex, reel] of this.reels.entries()) {
            const newSpinConfig = cloneDeep(spinConfig);
            newSpinConfig.bonusHit = bonusHitReels[reelIndex];
            if (anticipate[reelIndex]) {
                newSpinConfig.anticipationActive = true;
                newSpinConfig.baseSpinTime += accumulatedAnticipation;
                accumulatedAnticipation += anticipationTime + spinConfig.anticipationStopTimeIn + spinConfig.anticipationStopTimeOut;
                newSpinConfig.anticipationTime = anticipationTime;
                reel.setSpinConfig(newSpinConfig);
            }
            else {
                reel.setSpinConfig(newSpinConfig);
            }
        }
    }

    //find the stop position for each reel + swap the symbols on the map so they land on the result
    spin(spinResult: ReelSetSymbols): void {

        const stops = [];
        let stop;

        for (const [reelIndex, reel] of this.reels.entries()) {

            stop = reel.findReelStopIndex(spinResult[reelIndex]);
            stops.push(stop);

            /*
                provided result is reverse to reel strip order i.e:
                    stepping through a reel strip 7,3,6,4,1, with a stop index of 2 we'd expect
                    7,3,6 to be on the reels, with 6 at the top as it was the last symbol.
                    Instead we need to display 6,3,7, so reverse the provided result before subbing.
            */
            reel.swapReelStripSymbols(stop, cloneDeep(spinResult[reelIndex]).reverse());
        }

        this.spinToStops(stops);
    }

    slamStop(spinResult: ReelSetSymbols): void {

        for (const [i, reel] of this.reels.entries()) {
            reel.slamStop(spinResult[i]);
        }
        getSoundPlayer().reelStop();
        this.signals.slamStop.dispatch();
    }

    startWinAnims(animateState: boolean[][], totalMoneyWon: number, totalBet: number): void {
        const winToBet = totalMoneyWon / totalBet;
        for (const [i, reel] of this.reels.entries()) {
            if (winToBet > this.services.resources.getSetting(`symbolAnimationWinThreshold`)) {
                reel.startWinAnimations(animateState[i], true);
            }
        }
    }

    startScatterWinAnimationSequences(symbols: { column: number, row: number }[]) {

        for (const value of symbols) {
            this.reels[value.column].startScatterWinAnimationSequence(value.row);
        }
    }

    startScatterWinAnimationLoops(symbols: { column: number, row: number }[]) {

        for (const value of symbols) {
            this.reels[value.column].startScatterWinAnimationLoop(value.row);
        }
    }

    bonusCompleteAnimations(bonusSymbolsPositions: SymbolPos[]) {
        for (const bonusPos of bonusSymbolsPositions) {
            this.reels[bonusPos.column].bonusCompleteAnimation(bonusPos.row);
        }
    }

    bonusExtraFreeSpinsAnimations(bonusSymbolsPositions: SymbolPos[]) {
        for (const bonusPos of bonusSymbolsPositions) {
            this.reels[bonusPos.column].bonusExtraFreeSpinsAnimation(bonusPos.row);
        }
    }

    bonusInitiatedAnimations(bonusSymbolsPositions: SymbolPos[]) {
        for (const bonusPos of bonusSymbolsPositions) {
            this.reels[bonusPos.column].bonusInitiatedAnimation(bonusPos.row);
        }
    }

    stopWinAnims(): void {

        for (const reel of this.reels) {
            reel.stopWinAnimations();
        }
    }

    override reset(): void {
        this.anticipationView.reset();
        for (const reel of this.reels) {
            reel.reset();
        }
    }

    private setupSymbolCache(): SymbolCache {

        const { factory, resources } = this.services;

        const symbolCache: SymbolCache = {};
        const symbolSpec = resources.getSpec(`symbols`) as SymbolsSpec;

        for (const [key, spec] of Object.entries(symbolSpec)) {
            symbolCache[key] = new ElasticCache<Spine>(
                () => {
                    return factory.createSpine(spec);
                },
                () => {

                },
                () => {

                }
            );
        }

        return symbolCache;
    }

    private setupMask(layout: ReelsLayout): void {
        const padding = 3;
        const { x, y } = layout.mask;
        const { reelCount, symbolCount, symbolSize, reelSpacing } = layout;
        const width = symbolSize.width * reelCount + reelSpacing * (reelCount - 1) + padding;
        const height = symbolSize.height * symbolCount + padding;
        const graphic = drawRectCentered(width, height, 0xFFFFFF);
        graphic.x = x;
        graphic.y = y;

        this.reelContainer.addChild(graphic);
        this.reelContainer.mask = graphic;
    }

    private spinToStops(reelStops: number[]): void {
        for (const [i, reel] of this.reels.entries()) {
            reel.spin(reelStops[i]);
        }
    }

    private reelStopped(id: number): void {

        this.signals.reelStop.dispatch(id);

        if (id === this.reels.length - 1) {
            this.signals.spinComplete.dispatch();
        }
    }

    getAnticipationReels(spinResult: ReelSetSymbols, eventType: GameEvent): boolean[] {
        const anticipationReels: boolean[] = [];
        let currentAnticipation = 0;
        const anticipationThreshold = (eventType == GameEvent.INITIAL_SPIN) ? 2 : 1;
        spinResult.forEach((col: ReelSymbols) => {
            anticipationReels.push(currentAnticipation >= anticipationThreshold);
            if (col.includes(BonusSymbolID.B1)) {
                currentAnticipation++;
            }
        });
        return anticipationReels;
    }

    applyGamePadding(type?: GameEvent) {
        if (!type) type = this._lastGameEvent;
        this._lastGameEvent = type;
        const { layout, resources } = this.services;
        const padding = resources.getSetting(`bucketPadding`);
        const orientation = layout.orientation === `portrait` && type === GameEvent.FREE_SPIN ? `portraitFree` : layout.orientation;

        layout.setPadding(padding[orientation]);
    }


    private reorient(): void {
        const { factory, layout } = this.services;
        factory.applySpec(this, this.layout.position[layout.orientation]);
    }
}